import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AuthProvider, homeRouteFor, useAuth } from './state/AuthContext';
import { SoftphoneProvider } from './telephony/SoftphoneContext';
import { AppShell } from './components/AppShell';
import { Banner } from './components/ui';
import { Login } from './pages/Login';
import { CallTakerConsole } from './pages/CallTakerConsole';
import { IncidentsPage } from './pages/IncidentsPage';
import { IncidentDetail } from './pages/IncidentDetail';
import { DispatchConsole } from './pages/DispatchConsole';
import { FieldConsole } from './pages/FieldConsole';
import { Reports } from './pages/Reports';
import { AdminConsole } from './pages/AdminConsole';
import type { Permission } from './lib/types';

export function App() {
  return (
    <AuthProvider>
      <SoftphoneProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <AppShell>
                  <Routes>
                    <Route index element={<Home />} />
                    <Route path="calls" element={<Guard need={['calls.handle']}><CallTakerConsole /></Guard>} />
                    <Route path="incidents" element={<Guard need={['incidents.read']}><IncidentsPage /></Guard>} />
                    <Route path="incidents/:id" element={<Guard need={['incidents.read']}><IncidentDetail /></Guard>} />
                    <Route path="dispatch" element={<Guard need={['dispatch.view']}><DispatchConsole /></Guard>} />
                    <Route path="field" element={<FieldConsole />} />
                    <Route path="reports" element={<Guard need={['reports.own']}><Reports /></Guard>} />
                    <Route path="admin/*" element={<Guard need={[]} anyOf={['users.manage', 'system.config']}><AdminConsole /></Guard>} />
                    <Route path="*" element={<Banner kind="warn">That page does not exist.</Banner>} />
                  </Routes>
                </AppShell>
              </RequireAuth>
            }
          />
        </Routes>
      </SoftphoneProvider>
    </AuthProvider>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="empty">Restoring session…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.mustChangePassword) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function Home() {
  const { user } = useAuth();
  return user ? <Navigate to={homeRouteFor(user)} replace /> : null;
}

/**
 * Renders children only if the signed-in role holds the permissions. This is
 * navigation ergonomics, not security — every one of these endpoints refuses
 * the same request server-side.
 */
function Guard({
  need,
  anyOf,
  children,
}: {
  need: Permission[];
  anyOf?: Permission[];
  children: ReactNode;
}) {
  const { can } = useAuth();
  const allowed = can(...need) && (!anyOf || anyOf.some((permission) => can(permission)));
  if (!allowed) {
    return <Banner kind="warn">Your role does not have access to this console.</Banner>;
  }
  return <>{children}</>;
}
